import {
    FC,
    useEffect,
    useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { ConnectedHelpTooltip } from '../../@paco/connectors'
import trans from '../../@paco/helpers/trans'

import ItemsList from '../../components/ItemsList/ItemsList'
import TableContainer from '../../components/Table/TableContainer'
import { getYearsFromPeriods } from '../../helpers'
import { formatDate } from '../../helpers/date'
import { translate } from '../../helpers/translations/translator'
import { PayrollPeriodViewModel } from '../../models'
import { getPayrollPeriods } from '../../redux/app/appActions'
import { Reducers } from '../../redux/reducers'
import getPayrollPeriodEndDateLabel from '../../services/PayrollPeriodService/getPayrollPeriodEndDateLabel'
import { BasicSelectOption } from '../Schedule/components/CalendarToolbar/BasicSelect/BasicSelect'
import { PeriodYearSelect } from './components/PeriodYearSelect/PeriodYearSelect'
import { SettingsCardTitle } from './components/SettingsCard/SettingsCardTitle'

import './Periods.scss'

const Periods: FC = () => {
    const dispatch = useDispatch()
    const periods = useSelector((state: Reducers) => state.appReducer.payrollPeriods)
    const loading = useSelector((state: Reducers) => (
        state.appReducer.payrollPeriodsLoading || state.settingsReducer.loading))

    const [activeYear, setActiveYear] = useState(new Date().getUTCFullYear())
    const [years, setYears] = useState<number[]>([])

    const filteredPeriods = periods
        .filter((period: PayrollPeriodViewModel) => period.year === activeYear)
        .sort((
            a: PayrollPeriodViewModel,
            b: PayrollPeriodViewModel,
        ) => a.periodNumber - b.periodNumber)
    const yearOptions: BasicSelectOption[] = years.map(year => ({ label: year, value: year }))

    useEffect((): void => {
        dispatch(getPayrollPeriods())
    }, [])

    useEffect((): void => {
        setYears(getYearsFromPeriods(periods))
    }, [periods])

    const onPeriodYearChange = (value: number): void => {
        setActiveYear(value)
    }

    const renderTable = () => {
        const dateFormat = 'EEEEEE d MMM yyyy'
        const tableAttributes = [
            {
                label: translate('common.year'),
                customAction: (period: PayrollPeriodViewModel) => period.year,
                className: 'settings-period-table-td-year',
            },
            {
                label: translate('common.number'),
                customAction: (period: PayrollPeriodViewModel) => period.periodNumber,
                className: 'settings-period-table-td-number',
            },
            {
                label: translate('pages.tracks.startDate'),
                customAction: (period: PayrollPeriodViewModel) => formatDate(period.start, dateFormat),
                className: 'settings-period-table-td-start',
            },
            {
                label: translate('pages.tracks.endDate'),
                customAction: (period: PayrollPeriodViewModel) => getPayrollPeriodEndDateLabel(period, dateFormat),
                className: 'settings-period-table-td-end',
            },
        ]

        return (
            <div className={`card settings-card settings-period settings-period--length-${filteredPeriods.length}`}>
                <SettingsCardTitle title={translate('pages.settings.periods')}>
                    <PeriodYearSelect
                        selected={activeYear}
                        options={yearOptions}
                        onSelectChange={onPeriodYearChange}
                    />
                </SettingsCardTitle>
                <ItemsList
                    itemsLength={periods.length}
                    isLoading={loading}
                >
                    <TableContainer
                        className="settings-period-table"
                        attributes={tableAttributes}
                        data={filteredPeriods}
                    />
                </ItemsList>
            </div>
        )
    }

    return (
        <div className="periods-page">
            <ConnectedHelpTooltip
                index={0}
                route="periods"
                subTitle={trans('help.settings.periods.title')}
                text={trans('help.settings.periods.text')}
                title={trans('help.settings.title')}
                className="periods-page__help-tooltip"
            />
            <div className="periods-page__top-bar">
                <div className="periods-page__title">
                    {translate('pages.settings.periodsOverview')}
                </div>
            </div>
            {renderTable()}
        </div>
    )
}

export default Periods
